import { Accessor, Setter, createEffect, createSignal, debounce } from 'common';
import { SetStoreFunction, Store, createStore, reconcile } from 'solid-js/store';
import { DataStoreProperty, dataStore as api } from 'st-app/api'

export class DataStoreContext {
    private dataSource?: string;
    private primaryObject?: string;

    identity: Accessor<string | undefined>;
    setIdentity: Setter<string | undefined>;

    private data: Store<Record<string, Record<string, DataStoreProperty>>>;
    private setData: SetStoreFunction<Record<string, Record<string, DataStoreProperty>>>

    constructor(dataSource?: string, primaryObject?: string) {
        this.dataSource = dataSource;
        this.primaryObject = primaryObject;

        [this.identity, this.setIdentity] = createSignal<string>();
        [this.data, this.setData] = createStore<Record<string, Record<string, DataStoreProperty>>>({});

        createEffect((prev: string | undefined) => {
            if (prev) {
                window.requestAnimationFrame(() =>
                    this.setData(prev, reconcile({}))
                );
            }

            return this.identity();
        }, this.identity());
    }

    get<K extends keyof DataStoreProperty>(
        key: K,
        srcName: string,
        identity?: string
    ): DataStoreProperty[K] {
        const ident = identity ?? this.identity() ?? '';

        if (!this.data[ident]) {
            this.setData(ident, reconcile({}));
        }

        if (!this.data[ident][srcName]) {
            const dsProp = new DataStoreProperty();
            dsProp.srcName = srcName;

            this.setData(ident, srcName, reconcile({ ...dsProp }));
        }

        return this.data[ident][srcName][key];
    }

    private updateData(entries: Record<string, DataStoreProperty[]>) {
        for (const identity in entries) {
            for (const prop of entries[identity]) {
                if (!this.data[identity]) {
                    this.setData(identity, reconcile({}));
                }

                if (prop.srcName) {
                    this.setData(identity, prop.srcName, reconcile({ ...prop }));
                }
            }
        }
    }

    setCurrentValue(srcName: string, value?: string, identity?: string) {
        const ident = identity ?? this.identity() ?? '';
        const property = this.get('currentValue', srcName, ident);
        this.setData(ident, srcName, 'currentValue', value);
    }

    clear() {
        this.setData(reconcile({}));
    }

    private fetchRequests: any = {};
    async fetchValue(srcName: string, identity?: string) {
        const currentValue = this.get('currentValue', srcName, identity);
        if (currentValue !== undefined) {
            return Promise.resolve(currentValue);
        }

        return new Promise<string>((resolve, reject) => {
            if (!identity) {
                resolve('');
                return;
            }

            const fetchRequest = this.fetchRequests[identity] ?? {
                args: [],
                callback: debounce((args) => {
                    if (args.length == 0) return;

                    const props = args.map((arg: any) => arg.srcName);
                    const res = this.primaryObject
                        ? api.LoadData(this.primaryObject, identity!, props)
                        : api.LoadDataSource(this.dataSource!, identity!, props);

                    res.then((entries) => {
                        this.updateData(entries);
                        for (const arg of args) {
                            arg.resolve(this.get('currentValue', arg.srcName, identity));
                        }
                    });
                }, 10)
            };
            this.fetchRequests[identity] = fetchRequest;

            fetchRequest.args.push({
                srcName, resolve
            });
            fetchRequest.callback(fetchRequest.args);
        });
    }

    async load(identity: string) {
        this.setIdentity(identity);

        // The collection with the empty identity is assumed to be the initial state, which will
        // contain all possible properties for the container
        if (identity && this.data['']) {

            const props = Object.keys(this.data['']);
            if (this.primaryObject) {
                await api.LoadData(this.primaryObject, identity, props)
                    .then(this.updateData.bind(this));
            }
            else if (this.dataSource) {
                await api.LoadDataSource(this.dataSource, identity, props)
                    .then(this.updateData.bind(this));
            }
        }
        else {
            this.setData(reconcile({}));
        }
    }
}
